import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Utensils, ClipboardList, ChefHat, Settings, LogOut, Send, Building2 } from "lucide-react";
import Logo from "./Logo";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isPreparatore = user?.role === "preparatore" || user?.role === "admin" || user?.role === "superadmin";
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const isSuper = user?.role === "superadmin";

  const nav = [
    { to: "/menu", label: "Menu", icon: Utensils, show: true, testid: "nav-menu" },
    { to: "/le-mie-prenotazioni", label: "Prenotazioni", icon: ClipboardList, show: true, testid: "nav-orders" },
    { to: "/preparatore", label: "Cucina", icon: ChefHat, show: isPreparatore, testid: "nav-preparatore" },
    { to: "/admin", label: "Admin", icon: Settings, show: isAdmin, testid: "nav-admin" },
    { to: "/super-admin", label: "Super", icon: Building2, show: isSuper, testid: "nav-super" },
    { to: "/telegram", label: "Telegram", icon: Send, show: true, testid: "nav-telegram" },
  ];

  const officeName = user?.office?.name;

  return (
    <div className="min-h-screen bg-background bg-grain pb-20 md:pb-0">
      {/* Top bar (desktop) */}
      <header className="hidden md:block sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-8 h-16 flex items-center justify-between">
          <Link to="/menu" className="flex items-center gap-2.5" data-testid="brand-logo">
            <Logo size={28} />
            <div className="flex flex-col leading-tight">
              <span className="font-display font-semibold text-lg tracking-tight">
                Schiscetta Ufficio
              </span>
              {officeName && (
                <span className="text-[11px] text-muted-foreground" data-testid="header-office-name">
                  {officeName}
                </span>
              )}
            </div>
          </Link>
          <nav className="flex items-center gap-1">
            {nav.filter((n) => n.show).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 flex items-center gap-2 ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium" data-testid="header-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 rounded-md text-muted-foreground hover:text-accent hover:bg-muted transition-colors"
              aria-label="Esci"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="px-4 h-14 flex items-center justify-between">
          <Link to="/menu" className="flex items-center gap-2 min-w-0">
            <Logo size={22} />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-display font-semibold tracking-tight">Schiscetta</span>
              {officeName && (
                <span className="text-[10px] text-muted-foreground truncate" data-testid="header-office-name-mobile">
                  {officeName}
                </span>
              )}
            </div>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-button-mobile"
            className="text-muted-foreground p-2"
            aria-label="Esci"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="animate-fade-in">{children}</main>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border">
        <div className="grid grid-cols-5 max-w-md mx-auto">
          {nav.filter((n) => n.show).slice(0, 5).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/menu"}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-xs ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <n.icon className="w-5 h-5" />
              <span className="text-[10px]">{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
